// Тренажёр по теме: задача, ответ, разбор, следующая — и так сколько угодно.
//
// Отдельный экран, а не режим «Решения». Общего у них поле ввода и вердикт,
// разного — всё остальное: в наборе дня задача принадлежит дню, закрывается
// один раз и двигает владение, здесь задача не принадлежит ничему и не двигает
// ничего. Смешать их значило бы держать в одном экране два разных ответа на
// вопрос «что значит решённая задача». Та же причина, по которой свой экран у
// зачёта и у диагностики.
//
// **Счёт живёт здесь и нигде больше.** «Решено 7, верных 5» — состояние
// экрана: обновил страницу, счёт с нуля. Сервер его не хранит намеренно —
// тренажёр ничего не меряет, и таблица под этот счёт была бы таблицей ради
// числа, которое никому не нужно назавтра.
//
// **Разбор приходит после каждого ответа, в том числе верного** — как в наборе
// дня и по той же причине: сошедшийся ответ и верный метод разные вещи. И
// в отличие от зачёта, где обратная связь по ходу портит измерение: здесь
// измерять нечего.

import { useEffect, useRef, useState } from "react";
import { api, useMutation, type DrillNext, type DrillAnswer } from "../api";
import { equationMethod, MIXED_EQUATION_METHOD_ID } from "../../convex/lib/equationMethods";
import { newClock } from "../lib/clock";
import { Formula } from "../lib/Formula";
import { reasonOf } from "../lib/errors";
import { plural } from "../lib/dates";
import { IconBook } from "../ui/Icons";
import { Scratch } from "../ui/Scratch";
import { Verdict } from "../ui/Verdict";
import { lineSeed, verdictLine } from "../lib/verdict-lines";

/** Ответ, который уже проверен: под условием стоит он, а не поле ввода. */
type Checked = DrillAnswer & { raw: string; seed: number };

export function Drill({
  learnerKey,
  skillId,
  methodId,
  onToday,
  returnLabel = "К набору дня",
  onOpenTheory,
  onTestOut,
}: {
  learnerKey: string;
  skillId: string;
  methodId?: string;
  onToday: () => void;
  returnLabel?: string;
  /**
   * Правило темы. `open` едет вместе с ней: страница разбора кончается
   * кнопками по состоянию темы, и «Решать эту тему» у закрытой откажет после
   * нажатия. Тренажёр это состояние знает — сервер прислал его с задачей.
   */
  onOpenTheory: (skillId: string, open: boolean) => void;
  /** Зачёт по этой же теме: тренажёр её не открывает, а зачёт открывает. */
  onTestOut: (skillId: string) => void;
}) {
  const mixed = methodId === MIXED_EQUATION_METHOD_ID;
  const method = methodId === undefined ? undefined : equationMethod(methodId);
  const nextItem = useMutation(api.drill.next);
  const send = useMutation(api.drill.answer);

  const [page, setPage] = useState<DrillNext | null>(null);
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState<Checked | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [done, setDone] = useState(0);
  const [correct, setCorrect] = useState(0);
  const clock = useRef(newClock());
  // Ref, а не пустой массив зависимостей: StrictMode прогоняет эффект дважды,
  // и первая задача бралась бы из пула двумя разными. Тот же приём, что на
  // странице разбора темы.
  const asked = useRef<string | null>(null);

  useEffect(() => {
    const request = JSON.stringify([learnerKey, skillId, methodId]);
    if (asked.current === request) return;
    asked.current = request;
    setPage(null);
    setBusy(false);
    setChecked(null);
    setRaw("");
    setFailed(null);
    setDone(0);
    setCorrect(0);
    void nextItem({ learnerKey, skillId, ...(methodId === undefined ? {} : { methodId }) })
      .then((p) => {
        if (asked.current !== request) return;
        setPage(p);
        clock.current = newClock();
      })
      .catch((e: unknown) => {
        if (asked.current === request) setFailed(reasonOf(e));
      });
  }, [learnerKey, skillId, methodId, nextItem]);

  // Пауза, пока вкладка скрыта, — то же правило, что на экране задачи:
  // пятнадцатилетний решает в тетрадке и по четыре минуты не трогает
  // клавиатуру.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") clock.current.pause();
      else clock.current.resume();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const item = page?.item ?? null;

  const check = (): void => {
    if (busy || checked !== null || item === null || raw.trim().length === 0) return;
    setBusy(true);
    setFailed(null);
    void send({
      learnerKey,
      itemKey: item.itemKey,
      raw,
      ms: clock.current.elapsed(),
      msWall: clock.current.wall(),
    })
      .then((result) => {
        setBusy(false);
        // Опечатка — не ответ: задача остаётся, счёт не растёт, поле ждёт
        // поправки. То же, что в наборе дня.
        if (result.verdict === "unparsable") {
          setChecked(null);
          setFailed(
            `Не разобрано. Поле принимает ${item.inputHint}. Задача осталась — поправь ответ.`,
          );
          return;
        }
        setChecked({ ...result, raw, seed: lineSeed() });
        setDone((n) => n + 1);
        if (result.verdict === "correct") setCorrect((n) => n + 1);
      })
      .catch((e: unknown) => {
        setBusy(false);
        setFailed(reasonOf(e));
      });
  };

  /** Дальше: ключ уже известен из ответа, второй раз сервер не спрашиваем. */
  const forward = (): void => {
    const key = checked?.nextItemKey ?? null;
    setFailed(null);
    if (page === null || busy) return;
    setBusy(true);
    void nextItem({
      learnerKey, skillId,
      after: methodId === undefined ? key ?? item?.itemKey : item?.itemKey,
      ...(methodId === undefined ? {} : { methodId }),
    })
      .then((p) => {
        setBusy(false);
        setChecked(null);
        setRaw("");
        setPage(p);
        clock.current = newClock();
      })
      .catch((e: unknown) => {
        setBusy(false);
        setFailed(reasonOf(e));
      });
  };

  if (failed !== null && page === null) {
    return (
      <div className="stack">
        <div className="notice">{failed}</div>
        <div className="row row--actions">
          <button className="btn" onClick={onToday}>
            {returnLabel}
          </button>
        </div>
      </div>
    );
  }

  if (page === null) {
    return (
      <div className="stack">
        <div className="skeleton" style={{ width: 320, height: 34 }} />
        <div className="card">
          <div className="skeleton" />
        </div>
      </div>
    );
  }

  return (
    <div className="cols">
      <div className="col">
        <div>
          {/* Слово «Тренажёр» стоит в шапке приложения; здесь — тема. */}
          <h1 className="h1">{mixed ? "Выбор метода" : method?.title ?? page.name}</h1>
          {methodId !== undefined ? (
            <p className="muted">{mixed
              ? "Метод не назван: выбери его по условию. Разбор — после ответа."
              : "Отработка одного метода с разбором после каждого ответа."} Это тренировка, не зачёт: владение и набор дня не меняются.</p>
          ) : null}
          <p className="lede">
            {done === 0 && methodId === undefined ? (
              <>
                Задачи по одной теме подряд, с разбором после каждой. Ни набор
                дня, ни владение это не трогает — сколько решить, решаешь сам.
              </>
            ) : (
              <>
                Решено <span className="num">{done}</span>{" "}
                {plural(done, "задача", "задачи", "задач")} · верно{" "}
                <span className="num">{correct}</span>
              </>
            )}
          </p>
        </div>

        <div className="card">
          <div className="card__head">
            <div className="card__label">Условие</div>
            {page.theory && !mixed ? (
              <button
                className="btn btn--icon"
                title="Правило, разобранный пример и типичные ошибки этой темы"
                onClick={() => onOpenTheory(page.skillId, page.open)}
              >
                <IconBook size={17} />
                Правило темы
              </button>
            ) : null}
          </div>
          {item ? (
            <div className="statement">
              <Formula source={item.statement} chip />
            </div>
          ) : (
            <div className="skeleton" style={{ marginTop: 14, height: 26 }} />
          )}
        </div>

        {checked !== null ? (
          <>
            <Verdict
              correct={checked.verdict === "correct"}
              line={verdictLine(checked.verdict === "correct", checked.seed)}
              raw={checked.raw}
              answerDisp={checked.answerDisp}
              solution={checked.solution}
              unclear={
                item ? { learnerKey, itemKey: item.itemKey, where: "Тренажёр" } : undefined
              }
            />
            {failed !== null ? <div className="notice">{failed}</div> : null}
            <div className="row row--actions" style={{ marginTop: 16 }}>
              <button
                className="btn btn--primary btn--big"
                disabled={busy || (methodId === undefined && checked.nextItemKey === null)}
                onClick={forward}
              >
                {busy ? "Беру задачу" : "Следующая задача"}
              </button>
              <button className="btn btn--big" onClick={onToday}>
                Закончить
              </button>
            </div>
          </>
        ) : (
          <div className="card">
            <div className="card__label">Ответ</div>
            <div className="answer" style={{ marginTop: 14 }}>
              <span className="answer__label">Ответ</span>
              <input
                className={`field${failed !== null ? " field--bad" : ""}`}
                value={raw}
                autoFocus
                inputMode="text"
                autoComplete="off"
                spellCheck={false}
                placeholder={item?.inputHint ?? ""}
                onChange={(event) => {
                  setRaw(event.target.value);
                  if (failed !== null) setFailed(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") check();
                }}
              />
              <button
                className="btn btn--primary"
                disabled={busy || raw.trim().length === 0 || item === null}
                onClick={check}
              >
                {busy ? "Проверяю" : "Проверить"}
              </button>
            </div>
            {failed !== null ? (
              <div className="notice" style={{ marginTop: 12 }}>
                {failed}
              </div>
            ) : null}
            <div className="row row--actions" style={{ marginTop: 16 }}>
              <button className="link link--quiet" onClick={onToday}>
                Закончить
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="col">
        {/* Ключ по задаче: «одна задача — один лист», как в наборе дня. */}
        {item ? <Scratch key={item.itemKey} statement={item.statement} /> : null}

        <div className="card card--dashed">
          <div className="card__label">Что это меняет</div>
          <div className="muted" style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}>
            {mixed ? (
              <>Ничего. Это практика выбора метода, не оценка владения. Набор дня и навыки на карте не меняются.</>
            ) : page.open ? (
              <>
                Ничего. Тренажёр не идёт в набор дня и не двигает владение —
                владение растёт от задач набора, а не от тренировки. Зато
                задач здесь столько, сколько в теме есть.
              </>
            ) : (
              <>
                Ничего: тема на карте останется закрытой, сколько её ни решай.
                Открывает её зачёт — три задачи посложнее, без подсказок и без
                разбора до сдачи. Тренажёр к нему и готовит.
              </>
            )}
          </div>
          {page.open || mixed ? null : (
            <div className="row row--actions" style={{ marginTop: 12 }}>
              <button className="btn" onClick={() => onTestOut(page.skillId)}>
                Сдать зачёт по теме
              </button>
            </div>
          )}
        </div>

        <div className="card card--dashed">
          <div className="card__label">Как это проверяется</div>
          <div className="muted" style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}>
            Проверка серверная и мгновенная. Порядок корней не важен, дробь и
            десятичная — одно и то же число, кириллическая «х» принимается как «x».
          </div>
          <div className="faint" style={{ marginTop: 12, lineHeight: 1.6 }}>
            {mixed
              ? "После ответа будет разбор. Следующую задачу выбираем из разных семейств — метод снова нужно определить самому."
              : "Ошибся — следующая задача будет из того же семейства, пока ошибка ещё в голове."}
          </div>
        </div>
      </div>
    </div>
  );
}
